/* ============================================================================
   Enshield Design DNA — PROTECTION PATH (React)
   ----------------------------------------------------------------------------
   Two tellings of one motif.

   SUMMARY (Home, horizontal): the line draws itself once on entry over a fixed
   duration. The section is a glance, not a read — a scroll-driven line in a
   band this short would barely move before the reader had passed it.

   LONG FORM (How it works, vertical): the line follows the reader's scroll.
   Five stages with real depth take time to read, and a timed draw would run
   ahead of anyone actually reading them. Handing the pace to the reader is the
   idea adopted from the scroll-triggered process timelines surveyed in
   docs/public-web-design-research.md.

   Either way the stage states are derived from the same progress value that
   drives the line, so a node can never light before the line reaches it.

   Stages are `pending | active | done` rather than a boolean. The stage the
   line is currently at and a stage it has already passed are different things,
   and drawing them identically loses the sense of travel.

   Reduced motion: the line is fully drawn and every stage is done on mount.
   The end state is identical; only the travel is removed.
   ========================================================================== */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { animate, motion, useInView, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, type MotionValue } from 'framer-motion';
import { Icon, type IconName } from './icons';

export type PathStage = {
  label: string;
  detail: string;
  icon?: IconName;
  /** Optional depth for the long-form telling. */
  extra?: ReactNode;
};

export type StageState = 'pending' | 'active' | 'done';

/** Seconds for the summary line to travel the whole path. */
const DRAW_SECONDS = 1.5;
const LEAD_IN = 0.12;

/* -------------------------------------------------------------------------
   One stage. Derives its own state from the shared progress value, so no
   parent re-render is needed as the line travels — the whole path animates
   without React doing per-frame work.
   ------------------------------------------------------------------------- */
function Stage({
  stage,
  index,
  count,
  progress,
  reduced,
}: {
  stage: PathStage;
  index: number;
  count: number;
  progress: MotionValue<number>;
  reduced: boolean;
}) {
  // Where along the path this node sits.
  const at = count > 1 ? index / (count - 1) : 0;
  // How wide the "just arrived" band around this node is.
  const band = 1 / Math.max(count - 1, 1) / 1.5;

  const resolve = (value: number): StageState => {
    // Once the line is fully drawn every stage is complete. Without this the
    // last node sits at `at === 1` and can never be passed, so it stayed
    // highlighted as "you are here" long after the journey had finished.
    if (value >= 1) return 'done';
    if (value < at) return 'pending';
    return value < at + band ? 'active' : 'done';
  };

  /*
   * State is held in React rather than read straight off the MotionValue,
   * because a MotionValue can drive `style` but not an arbitrary attribute —
   * passing one to `data-state` renders "[object Object]". Subscribing costs
   * nothing per frame: this setter only fires on the two boundaries each
   * stage crosses, not on every scroll event.
   */
  const [state, setState] = useState<StageState>(() => (reduced ? 'done' : resolve(progress.get())));
  useMotionValueEvent(progress, 'change', (value) => {
    const next = reduced ? 'done' : resolve(value);
    setState((current) => (current === next ? current : next));
  });

  return (
    <li
      className="pp__stage"
      style={{ ['--pp-i' as string]: index }}
      data-state={state}
    >
      <span className="pp__node">
        <span className="pp__halo" aria-hidden="true" />
        {stage.icon ? <Icon name={stage.icon} /> : index + 1}
      </span>
      <span className="pp__label">{stage.label}</span>
      <span className="pp__detail">{stage.detail}</span>
      {stage.extra ? <div className="pp__extra">{stage.extra}</div> : null}
    </li>
  );
}

export function ProtectionPath({
  stages,
  label,
  vertical,
}: {
  stages: PathStage[];
  label?: string;
  /**
   * Keep the vertical spine at every width, and drive the line from scroll
   * rather than a timer. The horizontal form is a four-label summary; when
   * each stage carries real depth, the vertical form is the one that can hold
   * it — and it stays the same motif rather than a second implementation.
   */
  vertical?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const reduced = Boolean(reducedMotion);
  const ref = useRef<HTMLOListElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  // Long form: progress is the reader's position through the section.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 75%', 'end 60%'],
  });
  // Springing it stops the line twitching with trackpad jitter.
  const scrollProgress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });

  // Summary: progress is a one-shot tween on entry, driven explicitly rather
  // than by a numeric spring source — the latter left the value pinned at 0.
  const timedProgress = useMotionValue(reduced ? 1 : 0);
  useEffect(() => {
    if (vertical || reduced || !inView) return undefined;
    const controls = animate(timedProgress, 1, {
      duration: DRAW_SECONDS,
      delay: LEAD_IN,
      ease: [0.22, 0.78, 0.34, 1],
    });
    return () => controls.stop();
  }, [inView, reduced, vertical, timedProgress]);

  const progress = vertical && !reduced ? scrollProgress : timedProgress;

  return (
    /*
     * --pp-progress lives on the container because it inherits: every stage's
     * connector derives its own fill from this one value. CSS picks which axis
     * it scales, so the vertical and horizontal layouts share one animation
     * and a resize needs nothing re-resolved.
     */
    <motion.ol
      ref={ref}
      className={vertical ? 'pp pp--vertical' : 'pp'}
      data-motion="protection-path"
      data-motion-direction="sequence"
      aria-label={label}
      style={{
        ['--pp-count' as string]: stages.length,
        ['--pp-progress' as string]: reduced ? 1 : progress,
      }}
    >
      {stages.map((stage, index) => (
        <Stage
          key={stage.label}
          stage={stage}
          index={index}
          count={stages.length}
          progress={progress}
          reduced={reduced}
        />
      ))}
    </motion.ol>
  );
}
